import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type { RumpelstiltskinQualityId } from "./rumpelstiltskin-preset";

/**
 * /rumpelstiltskin-ai-video-generator 样片区的片子，做法同 chuttamalle-showcase.ts。
 *
 * 2026-10-26（GitHub #157）在本页重出：挂原梗 16:9，出片后服务端换上原声。人物是 gpt-image-2 出的虚构人物，
 * 1 条只传一张（少女留原片的样子）、1 条第二槽换成朋友。来源、积分和压制参数见
 * packages/scripts/rumpelstiltskin-showcase/README.md。站内出的 onSite = true，进 VideoObject。
 * 2026-10-27 第三条换成竞品样片（同 bulin-47-showcase.ts）：onSite false，不进 VideoObject，
 * 卡上不标来源和档位，也没有「Try this example」；来源只记在 source。
 *
 * R2 `home/showcase/rumpelstiltskin/` 下 clips/{id}.mp4、posters/{id}.jpg、refs/{id}-1.jpg；
 * 换朋友那条的图 2 是 refs/{id}-2.jpg，见 withFriend。清单为空时样片区整个不渲染。
 */
export interface RumpelstiltskinShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档，拼贴 hover 时显示档位名；外部片不写 */
	quality?: RumpelstiltskinQualityId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 出片用的原图，「Try this example」按顺序填进 hero 的槽，只有一张时第二槽清空；外部片没有 */
	inputImages?: string[];
	/** 外部片的出处，只做记录，不上页面 */
	source?: string;
}

const R2_BASE = "home/showcase/rumpelstiltskin";

/**
 * 视频和 poster 地址的版本参数。assets 域的 GET 走 Cloudflare 边缘缓存（max-age 一天），同一个 key
 * 覆盖上传后一天内还是旧文件；换片子时把这个数字加一
 */
const CLIP_VERSION = 5;

type Spec = Omit<
	RumpelstiltskinShowcaseClip,
	"videoUrl" | "poster" | "inputImages"
> & {
	/** 第二槽换成朋友（refs/{id}-2.jpg）；不写就是只传一张 */
	withFriend?: boolean;
};

/** 顺序即拼贴顺序：3 列、每列一条；第一条的 poster 也是特效目录和互推卡的封面 */
const SPECS: Spec[] = [
	{
		id: "redhead-standard",
		title: "Just One Photo",
		description:
			"A red-haired woman in a linen dress and laced bodice takes the little man's place in the barn clip: she shushes, spreads her arms and tiptoes toward the camera, grins, and dances in black pointed slippers while the original maiden cries.",
		quality: "standard",
		orientation: "landscape",
		onSite: true,
		duration: 5,
		hasAudio: true,
	},
	{
		id: "mustard-standard",
		title: "Swap in a Friend",
		description:
			"A woman with a black bob shushes, grins and tiptoes through the barn in her mustard sweater while her friend, swapped in for the maiden, watches with a hand over his mouth.",
		quality: "standard",
		orientation: "landscape",
		onSite: true,
		duration: 9,
		hasAudio: true,
		withFriend: true,
	},
	{
		id: "tigers-tee",
		title: "Tigers Tee",
		description:
			"A blonde woman in an oversized Tigers T-shirt shushes, grins, spreads her arms and tiptoes through the straw barn in black pointed slippers while the original maiden cries.",
		orientation: "landscape",
		onSite: false,
		duration: 9,
		hasAudio: true,
		source: "media.io (Rumpelstiltskin Dance, Seedance 2.5 edit, 2026-10-27 抓取)",
	},
];

const refUrl = (id: string, slot: 1 | 2) =>
	buildR2AssetsImageUrl(`${R2_BASE}/refs/${id}-${slot}.jpg`);

export const rumpelstiltskinShowcaseClips: RumpelstiltskinShowcaseClip[] =
	SPECS.map(({ withFriend, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(
			`${R2_BASE}/posters/${spec.id}.jpg?v=${CLIP_VERSION}`,
		),
		inputImages: spec.onSite
			? [refUrl(spec.id, 1), ...(withFriend ? [refUrl(spec.id, 2)] : [])]
			: undefined,
	}));

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const RUMPELSTILTSKIN_SHOWCASE_UPLOADED_AT = "2026-10-26T00:00:00+08:00";
